import { useId, useMemo, useState } from "react";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { extractUrls, stripTracking, domainOf, toCsv } from "./extract";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { copyText } from "@/lib/copyText";


const TOOL_INFO_CONTENT = {
  what: `Scans a block of text or HTML and pulls out every web link it finds, useful for collecting all the links mentioned in a document, email thread, or page source without manually searching for and copying each one individually.`,
  how: `Paste your text or HTML, and every detected URL is listed automatically. Copy the list or download it as a CSV.`,
  tip: `This catches links starting with http://, https://, ftp:// or www. — a bare domain such as "example.com" typed on its own isn't picked up, since it can't be reliably told apart from an ordinary word.`,
};

export default function UrlExtractor() {
  const idBase = useId();
  const [text, setText] = useState("");
  const [strip, setStrip] = useState(false);
  const [sortAz, setSortAz] = useState(false);
  const [domains, setDomains] = useState(false);

  const urls = useMemo(() => {
    let list = extractUrls(text);
    if (strip) list = Array.from(new Set(list.map(stripTracking)));
    if (domains) list = Array.from(new Set(list.map(domainOf)));
    if (sortAz) list = [...list].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
    return list;
  }, [text, strip, sortAz, domains]);

  const copyAll = () => {
    void copyText(urls.join("\n"), "Copied all URLs to clipboard");
  };

  const downloadCsv = () => {
    downloadBlob(new Blob([toCsv(["url", "domain"], urls)], { type: "text/csv" }), "extracted-urls.csv");
  };

  const { blocked } = useLogToolUsage(true, 3000, { ownActions: true });


  return (
    <ToolContainer>
      <Textarea
        placeholder="Paste any text, HTML, or document content here — we'll pull out every link..."
        value={text}
        onChange={e => setText(e.target.value)}
        aria-label="Text to scan"
        className="min-h-[200px]"
      />
      <div className="flex flex-wrap gap-x-5 gap-y-2 mt-3 text-sm">
        {([
          ["Remove tracking (utm_*, fbclid…)", strip, setStrip],
          ["Sort A–Z", sortAz, setSortAz],
          ["Unique domains only", domains, setDomains],
        ] as const).map(([label, val, set], i) => (
          <div key={label} className="flex items-center gap-2"><Switch id={`${idBase}-${i}`} checked={val} onCheckedChange={set} /><Label htmlFor={`${idBase}-${i}`} className="text-sm cursor-pointer">{label}</Label></div>
        ))}
      </div>
      <div className="flex items-center justify-between mt-4 mb-2">
        <p className="text-sm text-muted-foreground">
          {blocked ? "— blocked at today's usage limit —" : `${urls.length} unique URL${urls.length === 1 ? "" : "s"} found`}
        </p>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={copyAll}
            disabled={!urls.length || blocked}
          >
            Copy All
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={downloadCsv}
            disabled={!urls.length || blocked}
          >
            Download CSV
          </Button>
        </div>
      </div>
      {!blocked && urls.length > 0 && (
        <div className="rounded-lg border border-border p-4 bg-muted/30 max-h-[300px] overflow-y-auto">
          <ul className="space-y-1 font-mono text-sm break-all">
            {urls.map(url => (
              <li key={url}>{url}</li>
            ))}
          </ul>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
